import { Suspense, useState } from "react";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { ButtonBase } from "src/components/Button/ButtonBase";
import { DefaultErrorBoundary } from "src/components/DefaultErrorBoundary/DefaultErrorBoundary";
import { Dialog } from "src/components/Dialog/Dialog";
import { Icon } from "src/components/Icon/Icon";
import { border_radius } from "src/design-system/tokens/border";
import { useTripPlannerContext } from "src/domain/TripPlanner/hooks/useTripPlannerContext";
import { LazyMap, MapsApiProvider } from "src/MapPane";
import { Expand } from "src/svg/tripplanner/Expand";
import { iconSize, spacing, zIndex } from "src/theme";
import useSearch from "src/utils/hooks/useSearch";
import styled from "styled-components";
import color from "src/design-system/tokens/color";
import X from "src/design-system/icons/X.svg?react";
import { useIntl } from "react-intl";
import { MapLoadingPlaceholder } from "../MapLoadingPlaceholder";
import messages from "./TripPlannerMap.messages";
import { MapError } from "./util/MapError";

export function TripPlannerMobileMap() {
  const intl = useIntl();
  const { searchResponse } = useSearch();
  const { loadingIndex } = useTripPlannerContext();
  const [errorHidden, setErrorHidden] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const isLoading = !searchResponse || loadingIndex?.length;

  const MapContainer = isExpanded ? MapExpanded : MapContracted;

  function mapViewCloseCallback() {
    sendAnalyticsInteractionEvent({
      category: "TripPlannerMap",
      action: "Click:Close",
    });
    setIsExpanded(false);
  }

  function mapViewExpandCallback() {
    sendAnalyticsInteractionEvent({
      category: "TripPlanner",
      action: "Click:Map",
    });
    setIsExpanded(true);
  }

  return (
    <DefaultErrorBoundary
      ErrorFallback={
        !errorHidden
          ? () => <MapError hideCallback={() => setErrorHidden(true)} />
          : undefined
      }
    >
      <MapContainer
        isOpen={isExpanded}
        backdropClickCallback={mapViewCloseCallback}
        setOpen={mapViewExpandCallback}
      >
        <MapsApiProvider>
          <MapLoadingPlaceholder />
          {!isLoading && (
            <Suspense fallback={null}>
              {searchResponse && <LazyMap searchResponse={searchResponse} />}
            </Suspense>
          )}
          <ExpandContractButton
            expanded={isExpanded}
            onClick={isExpanded ? mapViewCloseCallback : undefined}
          >
            <>
              {isExpanded ? (
                <X
                  title={intl.formatMessage(messages.closeMobileMap)}
                  color={color.icon.primary}
                />
              ) : (
                <Icon size="md">
                  <Expand
                    title={intl.formatMessage(messages.expandMobileMap)}
                    tint="n300"
                  />
                </Icon>
              )}
            </>
          </ExpandContractButton>
        </MapsApiProvider>
      </MapContainer>
    </DefaultErrorBoundary>
  );
}

type MapExpandedProps = {
  isOpen: boolean;
  children: React.ReactNode;
  backdropClickCallback: () => void;
};

function MapExpanded({
  isOpen,
  children,
  backdropClickCallback,
}: MapExpandedProps) {
  return (
    <Dialog
      isOpen={isOpen}
      onBackdropClicked={backdropClickCallback}
      variant="fullScreen"
      backgroundColor="white"
      overflow="hidden"
    >
      {children}
    </Dialog>
  );
}

type MapContractedProps = {
  children: React.ReactNode;
  setOpen: VoidFunction;
};

function MapContracted({ children, setOpen }: MapContractedProps) {
  return (
    <MapContainerContracted data-testid="map-contracted" onClick={setOpen}>
      {children}
    </MapContainerContracted>
  );
}

const ExpandContractButton = styled(ButtonBase)<{ expanded: boolean }>`
  position: absolute;
  bottom: ${spacing.md};
  right: ${spacing.md};
  z-index: ${zIndex.map};

  width: ${iconSize.xxxxl};
  height: ${iconSize.xxxxl};
  border-radius: 50%;
  background-color: ${color.primitive.neutral100};
  filter: drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.15));
  ${({ expanded }) => expanded && `top: ${spacing.xl}; bottom: auto;`};
`;

const MapContainerContracted = styled.div`
  position: relative;
  height: 140px;
  border-radius: ${border_radius.rounded_md};
  overflow: hidden;

  // Some safari versions won't render border-radius without this
  transform: translateZ(0);

  // We don't want the map to be interactive when it's contracted
  .gm-style {
    pointer-events: none;
  }
`;
